import type { TodoCardView, TodoColumnValue } from "@portal/schemas";

/** 列の並びと見出し(SPEC §10。列はポータル独自)。 */
export const TODO_COLUMNS: readonly { id: TodoColumnValue; label: string }[] = [
  { id: "TODO", label: "未着手" },
  { id: "DOING", label: "進行中" },
  { id: "DONE", label: "完了" },
];

export const columnLabel = (id: TodoColumnValue) =>
  TODO_COLUMNS.find((c) => c.id === id)?.label ?? id;

export function cardsOf(cards: readonly TodoCardView[], column: TodoColumnValue): TodoCardView[] {
  return cards.filter((c) => c.column === column);
}

/**
 * カードを列の index 番目へ動かした結果(サーバーの moveTodoCard と同じ規則)。
 * 画面で先に並べ替えて見せるために使い、保存後はサーバーの結果で置き換える。
 */
export function moveCard(
  cards: readonly TodoCardView[],
  id: string,
  column: TodoColumnValue,
  index: number,
): TodoCardView[] {
  const card = cards.find((c) => c.id === id);
  if (!card) return [...cards];
  const rest = cards.filter((c) => c.id !== id);
  const inColumn = rest.filter((c) => c.column === column);
  const at = Math.max(0, Math.min(index, inColumn.length));
  const before = inColumn[at];
  const last = inColumn[inColumn.length - 1];
  const pos = before ? rest.indexOf(before) : last ? rest.indexOf(last) + 1 : rest.length;
  const next = [...rest];
  next.splice(pos, 0, { ...card, column });
  return next;
}

const COLUMN_DROP_PREFIX = "column:";
export const columnDropId = (column: TodoColumnValue) => `${COLUMN_DROP_PREFIX}${column}`;

/**
 * ドラッグを離した先(列そのもの、または列の中のカード)から、動かす先の列と位置を決める。
 * 列に落としたら末尾、カードに落としたらそのカードの位置。動かないなら null。
 */
export function dropTarget(
  cards: readonly TodoCardView[],
  activeId: string,
  overId: string,
): { column: TodoColumnValue; index: number } | null {
  const active = cards.find((c) => c.id === activeId);
  if (!active) return null;
  if (overId.startsWith(COLUMN_DROP_PREFIX)) {
    const column = overId.slice(COLUMN_DROP_PREFIX.length) as TodoColumnValue;
    const index = cardsOf(cards, column).filter((c) => c.id !== activeId).length;
    if (column === active.column && index === cardsOf(cards, column).indexOf(active)) return null;
    return { column, index };
  }
  const over = cards.find((c) => c.id === overId);
  if (!over || over.id === active.id) return null;
  return { column: over.column, index: cardsOf(cards, over.column).indexOf(over) };
}

/** 期限の表示。today は日本時間の YYYY-MM-DD。過ぎたもの・今日・明日を区別する。 */
export function dueLabel(
  due: string,
  today: string,
): { text: string; tone: "overdue" | "soon" | "normal" } {
  const days = Math.round(
    (Date.parse(`${due}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000,
  );
  const [, m, d] = due.split("-").map(Number);
  const date = `${m}/${d}`;
  if (days < 0) return { text: `${date} 期限切れ`, tone: "overdue" };
  if (days === 0) return { text: `${date} 今日まで`, tone: "soon" };
  if (days === 1) return { text: `${date} 明日まで`, tone: "soon" };
  return { text: `${date} まで`, tone: "normal" };
}

export const jstToday = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Tokyo" });
